<script lang="ts">
    import { onMount } from 'svelte';

    import MultiSelectWide from '../../shared/MultiSelectWide.svelte';
    import { vehiclesManager } from '../Vehicles/VehiclesApp/vehiclesManager';
    import { EasyVrpApp } from './EasyVrpApp';
    import type { Entry } from '../telematics/components/MultiSelectWide/types';
    import IconPending from '../../shared/Icons/IconPending.svelte';
    import ButtonSmallBlack from '../../shared/ButtonSmallBlack.svelte';
    import { EasyVrpApi } from './EasyVrpApi';
    import { api, customerSettings, mla } from '../../../stores/api';
    import TelematicsSliderSingle from '../TelematicsSlider/TelematicsSliderSingle.svelte';
    import { onMapReady } from '../../utilities/onMapReady';
    import { debug } from '../../../stores/store';
    import { MenuContextCustomActions } from '../MenuDropdown/MenuContextCustomActions';
    import { openModal } from 'svelte-modals';
    import IconCheck from '../../shared/Icons/IconCheck.svelte';
    import { DEPOT_MODE, SOLVER_TYPE, VEHICLE_MODE } from './EasyVrpApp.types';
    import ModalCustom from '../../shared/Modals/ModalCustom.svelte';
    import { SettingsManager } from '../Settings/SettingsManager';
    import TemplateSettingsGeneral from '../Settings/TemplateSettingsGeneral.svelte';
    import IconSettings from '../../shared/Icons/IconSettings.svelte';
    import ModalSettingsRouteCostWrapper from './ModalSettingsRouteCostWrapper.svelte';
    import ModalSettingsServiceTimeWrapper from './ModalSettingsServiceTimeWrapper.svelte';
    import ModalSettingsRouteSendTimeWrapper from './ModalSettingsRouteSendTimeWrapper.svelte';
    import Switch from '../../shared/Switch.svelte';
    import { calcSumHoursFromOpenHours, convertMinutesToStringTime, convertStringTimeToMinutes } from './EasyVrpUtils';
    import Select from '../../shared/Select.svelte';
    import ButtonSmallDanger from '../../shared/ButtonSmallDanger.svelte';
    import { captureException } from '@sentry/svelte';

    export let app: EasyVrpApp;

    let availableVehicles: Entry[] | null = null;
    let processing = false;
    let validateErrors: string[] = [];
    let selectedVehicleIds: string[] =
        app.getSelectedGroupedVehicleIds()?.map(id => id.toString()) || [];
    let maxAvailableVehicles: number = 0;

    let menu: MenuContextCustomActions = new MenuContextCustomActions();

    function updateMenu(menu: MenuContextCustomActions) {
        menu.reset();
        menu.addAction({
            id: 'internal_solver',
            label: 'algorytm wewnętrzny',
            disabled: false,
            visible: true,
            icon: app.getOptions().solver_type === SOLVER_TYPE.INTERNAL ? IconCheck : null,
            onClick: () => {
                app.setSolverType(SOLVER_TYPE.INTERNAL);
                updateMenu(menu);
                app = app; // Trigger reactivity
            },
            subMenuIsOpen: false,
            visibleInContextMenu: true,
        });
        menu.addAction({
            id: 'external_solver',
            label: 'algorytm zewnętrzny',
            disabled: false,
            visible: true,
            icon: app.getOptions().solver_type === SOLVER_TYPE.EXTERNAL ? IconCheck : null,
            onClick: () => {
                app.setSolverType(SOLVER_TYPE.EXTERNAL);
                updateMenu(menu);
                app = app; // Trigger reactivity
            },
            subMenuIsOpen: false,
            visibleInContextMenu: true,
        });
        menu = menu;
    }

    onMount(() => {
        updateMenu(menu);
        console.log('PageAdminSettings mounted');
        Promise.all([onMapReady()]).then(() => {
            $vehiclesManager
                .asyncGetItems(null)
                .then(vv => {
                    availableVehicles = vv.map(vehicle => ({
                        id: vehicle.getId()?.toString() || '',
                        name: vehicle.getName(),
                    }));
                    maxAvailableVehicles = vv.filter(v => !v.get()?.is_group).length;
                    if (app.getSelectedGroupedVehicleIds() === null) {
                        app.setSelectedGroupedVehicleIds(
                            availableVehicles.map(vehicle => parseInt(vehicle.id)),
                        );
                        $vehiclesManager
                            .getUniqueVehicleIds(app.getSelectedVehicleIds())
                            .then(ids => {
                                app.setMaxVehicles(ids?.length ?? 0);
                                app.setSelectedVehicleIds(ids);
                                app = app; // Trigger reactivity
                            });
                    }
                    calcNuclei(true);
                    console.log('Available vehicles:', availableVehicles);
                })
                .catch(error => {
                    console.error('Error fetching vehicles:', error);
                })
                .finally(() => {
                    formValidated = validateForm(app);
                });
        });
    });

    function calcNuclei(zoomToBounds: boolean = false) {
        console.log('#map Przelicz clicked - calcNuclei', app.getOptions());
        app.getMapManager().clearMapElements();

        if (app.getOptions().max_vehicles === 0) {
            console.warn('Max vehicles is set to 0, cannot proceed with calculation.');
            return;
        }
        if (app.getSelectedVehicleIds().length === 0) {
            console.warn('No vehicles selected, cannot proceed with calculation.');
            return;
        }
        processing = true;
        EasyVrpApi.calculateNuclei($api, app.getOptions())
            .then(result => {
                console.log('Calculation result:', result);
                app.getMapManager().nucleiManager.addNucleiToMapFromQuickCalc(
                    result,
                    $vehiclesManager.getItems(),
                    zoomToBounds,
                );
                app = app; // Trigger reactivity
            })
            .catch(error => {
                console.error('Error during calculation:', error);
            })
            .finally(() => {
                processing = false;
            });
    }

    function getCalcResults() {
        processing = true;
        let requestId = app.getVrpCalcId();
        if (!requestId) {
            console.warn('No request ID available, cannot fetch results.');
            processing = false;
            return;
        }
        EasyVrpApi.getAutodispatchResults($api, requestId)
            .then(result => {
                if (result.status === 'pending') {
                    console.info('Calculation is still pending, please try again later.');
                    return;
                }
                console.log('Autodispatch results:', result);
                app.clearVrpCalcId();
                app = app;
            })
            .catch(error => {
                app.clearVrpCalcId();
                app = app;
                console.error('Error fetching autodispatch results:', error);
            })
            .finally(() => {
                processing = false;
            });
    }

    function calcRoutes() {
        processing = true;
        EasyVrpApi.calculateAutodispatch($api, app.getOptions())
            .then(result => {
                console.log('Autodispatch result:', result);
                app.setVrpCalcId(result.id);
                // app.addNucleiToMapFromQuickCalc($vehiclesManager.getItems(), true);
                app = app; // Trigger reactivity
                app.getResultsApp()
                    .loadResults($api)
                    .then(() => {
                        processing = false;
                        app = app; // Trigger reactivity
                    })
                    .catch(error => {
                        console.error('Error loading results:', error);
                        processing = false;
                    });
            })
            .catch(error => {
                console.error('Error during autodispatch calculation:', error);
                captureException(error);
            })
            .finally(() => {
                processing = false;
            });
    }

    const validateForm = (app: EasyVrpApp) => {
        validateErrors = [];

        let formValidated = false;
        let maxVehicles = app.getMaxVehicles();
        let selectedVehicles = app.getSelectedVehicleIds().length;

        if (availableVehicles === null) {
            formValidated = false;
            validateErrors.push('Ładowanie dostępnych pojazdów w toku.');
        } else if (availableVehicles.length === 0) {
            formValidated = false;
            validateErrors.push('Brak dostępnych pojazdów do przydzielenia.');
        } else {
            formValidated = true;
        }

        if (!formValidated) {
            return false;
        }

        if (selectedVehicles === 0) {
            formValidated = false;
            validateErrors.push('Wybierz przynajmniej jeden pojazd do przydzielenia.');
        }

        if (!formValidated) {
            return false;
        }

        if (maxVehicles !== null && selectedVehicles < maxVehicles) {
            formValidated = false;
            validateErrors.push(
                `Wybranych pojazdów (${app.getSelectedVehicleIds().length}) jest mniej niż wyznaczony limit (${app.getMaxVehicles()}). Dodaj kilka pojazdów lub zmień limit.`,
            );
        }

        console.log(
            'Form validation status:',
            formValidated,
            maxVehicles,
            selectedVehicles,
            validateErrors,
        );
        return formValidated;
    };

    let formValidated = false;

    $: {
        formValidated = validateForm(app);
    }
    $: selectedVehicleIds = app.getSelectedGroupedVehicleIds().map(id => id.toString());
</script>

<div class:processing style="display: flex; flex-direction: column; gap: 10px;">
    <div style="display: flex; justify-content: flex-end; align-items: center; min-height: 58px;">
        {#if $debug}
            <ButtonSmallBlack
                text="Reload map layer"
                on:click={() => {
                    $mla.reloadLayer(Targeo.Base.Map, 'vrp');
                }}
            />
        {/if}

        {#if $debug}
            <ButtonSmallBlack
                text="checkAllUnfinishedCalculations"
                disabled={false}
                on:click={() => {
                    EasyVrpApi.checkAllUnfinishedCalculations($api)
                        .then(_ => {
                            console.log('Unfinished calculations checked successfully.', _);
                        })
                        .catch(error => {
                            console.error('Error checking unfinished calculations:', error);
                        });
                }}
            />
        {/if}

        {#if $debug}
            <ButtonSmallBlack
                text="Przelicz hexy (debug)"
                on:click={() => {
                    processing = true;
                    EasyVrpApi.createHexes($api)
                        .then(result => {
                            console.log('Hexes created:', result);
                        })
                        .catch(error => {
                            console.error('Error creating hexes:', error);
                        })
                        .finally(() => {
                            processing = false;
                            $mla.reloadLayer(Targeo.Base.Map, 'vrp');
                        });
                }}
            />
        {/if}

        {#if $debug}
            <ButtonSmallBlack
                disabled={availableVehicles === null ||
                    processing ||
                    app.getSelectedVehicleIds()?.length === 0}
                text="Przelicz jądra (debug)"
                on:click={() => {
                    calcNuclei(false);
                }}
            />
            <ButtonSmallBlack
                disabled={availableVehicles === null || processing || !app.getVrpCalcId()}
                text={'Get results #' + app.getVrpCalcId() + ' (debug)'}
                on:click={() => {
                    getCalcResults();
                }}
            />
        {/if}
        <div
            style="display: flex; flex-direction: row; align-items: center; gap: 5px; min-height: 80px;"
        >
            <div>
                {#if validateErrors.length > 0}
                    <div style="color: #cb0936; font-size: 13px;padding: 0 20px;">
                        <ul style="padding: 0;">
                            {#each validateErrors as error}
                                <li>{error}</li>
                            {/each}
                        </ul>
                    </div>
                {/if}
            </div>
            <div style="display: flex; align-items: center; gap: 5px; flex-direction: row-reverse;">
                {#if app.getOptions().get_jobs_only_for_selected_day}
                <ButtonSmallBlack
                    disabled={!formValidated || processing}
                    text="Wyznacz trasy dla zleceń na dzisiaj: {app.getCountStats().unassigned_jobs}"
                    on:click={() => {
                        //  ({app.getOptions().solver_type === SOLVER_TYPE.INTERNAL ? 'alg. wewn.': 'alg. zewn.'})
                        calcRoutes();
                    }}
                />
                    {:else}
                    <ButtonSmallDanger
                        disabled={!formValidated || processing}
                        text="Wyznacz trasy dla WSZYSTKICH zleceń: {app.getCountStats().unassigned_jobs}"
                        on:click={() => {
                        //  ({app.getOptions().solver_type === SOLVER_TYPE.INTERNAL ? 'alg. wewn.': 'alg. zewn.'})
                        calcRoutes();
                    }}
                    />
                    {/if}
                {#if processing}
                    <IconPending spin={true} scale={0.6} />
                {:else}
                    <!--                    <MenuContextIcon {menu} iconSymbol={IconMoreVert} menuMode="tooltip" />-->
                    <Select
                        selected={app.getOptions().solver_type}
                        items={[
                            { id: SOLVER_TYPE.INTERNAL, label: 'Algorytm wewnętrzny' },
                            { id: SOLVER_TYPE.EXTERNAL, label: 'Algorytm zewnętrzny' },
                        ]}
                        onChange={selectedId => {
                            app.setSolverType(selectedId);
                            // updateMenu(menu);
                            app = app; // Trigger reactivity
                            return true;
                        }}
                    />
                {/if}
            </div>
        </div>
    </div>

    <div style="padding-right: 20px; overflow-y: auto; height: calc(-270px + 100vh);">
        <TemplateSettingsGeneral>
            <div slot="label" style="display: flex; gap: 10px; align-items: center;">
                <div>
                    Pojazdy do przydzielenia ({app.getSelectedVehicleIds()
                    .length}/{maxAvailableVehicles})
                </div>
                <div></div>
            </div>
            <div slot="content">
                <div class="zone-param">
                    {#if availableVehicles === null}
                        <IconPending spin={true} />
                    {:else}
                        <div>
                            <MultiSelectWide
                                minAllowedSelectedItems={0}
                                availableEntries={availableVehicles}
                                selectedIds={selectedVehicleIds}
                                disabled={false}
                                onChange={selectedIds => {
                                    console.log('MultiSelectWide onChange', selectedIds);
                                    app.setSelectedGroupedVehicleIds(
                                        selectedIds.map(id => parseInt(id))
                                    );
                                    $vehiclesManager
                                        .getUniqueVehicleIds(app.getSelectedGroupedVehicleIds())
                                        .then(ids => {
                                            app.setSelectedVehicleIds(ids);
                                            app = app; // Trigger reactivity
                                        });
                                    calcNuclei(true);
                                    app = app;
                                }}
                            />
                        </div>
                    {/if}
                </div>
            </div>
        </TemplateSettingsGeneral>

        {#if availableVehicles}
            {#if $debug}
                <div
                    style="background-color: #f0f0f0; padding: 10px; border-radius: 5px; display: flex; flex-direction: column; gap: 10px;"
                >
                    <div class="zone-param">
                        <Switch
                            label="Użyj tylko zleceń z bieżącego dnia (obsługa dnia) (get_jobs_only_for_selected_day)"
                            checked={app.getOptions().get_jobs_only_for_selected_day}
                            on:change={e => {
                                app.getOptions().get_jobs_only_for_selected_day = e.detail.checked;
                                app.triggerChange();
                                app.loadCountStats($api).then(_ => {
                                    app = app;
                                });
                                app = app; // Trigger reactivity
                            }}
                        />
                    </div>
                    <div class="zone-param">
                        <Switch
                            label="Keep debug data"
                            checked={app.getOptions().keep_debug_data}
                            on:change={e => {
                                app.getOptions().keep_debug_data = e.detail.checked;
                                                                    app.triggerChange();
                                app = app; // Trigger reactivity
                            }}
                        />
                    </div>
                    <div class="zone-param">
                        <Switch
                            label="Enable merging jobs while solving"
                            checked={app.getOptions().enable_merging_jobs_while_solving}
                            on:change={e => {
                                app.getOptions().enable_merging_jobs_while_solving =
                                    e.detail.checked;
                                                                    app.triggerChange();
                                app = app; // Trigger reactivity
                            }}
                        />
                    </div>
                    <div class="zone-param">
                        <Switch
                            label="Sztywna liczba pojazdów (fleet fixed)"
                            checked={app.getOptions().fleet_fixed}
                            on:change={e => {
                                app.getOptions().fleet_fixed = e.detail.checked;
                                                                    app.triggerChange();
                                app = app; // Trigger reactivity
                            }}
                        />
                    </div>
                    <div class="zone-param">
                        <Switch
                            disabled={true}
                            label="Vehicle limit - nie dopuszczaj do przekroczenia limitów pojemności pojazdów"
                            checked={app.getOptions().options_layers_vehicleLimit_enabled}
                            on:change={e => {
                                app.getOptions().options_layers_vehicleLimit_enabled =
                                    e.detail.checked;
                                                                    app.triggerChange();
                                app = app; // Trigger reactivity
                            }}
                        />
                    </div>

                    <!--                <div class="zone-param">-->
                    <!--                    <span>Limit ładowności pojazdów (kg)</span>-->
                    <!--                    <div class="slider">-->
                    <!--                        <TelematicsSliderSingle-->
                    <!--                            props={{-->
                    <!--                                min: app.getOptionsDetails('max_vehicle_capacity_kg').min,-->
                    <!--                                max: app.getOptionsDetails('max_vehicle_capacity_kg').max,-->
                    <!--                                currentValue: app.getOptions().max_vehicle_capacity_kg,-->
                    <!--                                inputsAboveSlider: true,-->
                    <!--                                unit: '',-->
                    <!--                                roundValues: true,-->
                    <!--                                sliderWidth: 300,-->
                    <!--                            }}-->
                    <!--                            onChange={val => {-->
                    <!--                                app.getOptions().max_vehicle_capacity_kg = val;-->
                    <!--                                app.triggerChange();-->
                    <!--                                app = app; // Trigger reactivity-->
                    <!--                                calcNuclei(false);-->
                    <!--                            }}-->
                    <!--                        />-->
                    <!--                    </div>-->
                    <!--                </div>-->
                    <!--                <div class="zone-param">-->
                    <!--                    <span>Limit ładowności pojazdów (paczki)</span>-->
                    <!--                    <div class="slider">-->
                    <!--                        <TelematicsSliderSingle-->
                    <!--                            props={{-->
                    <!--                                min: app.getOptionsDetails('max_vehicle_capacity_packages').min,-->
                    <!--                                max: app.getOptionsDetails('max_vehicle_capacity_packages').max,-->
                    <!--                                currentValue: app.getOptions().max_vehicle_capacity_packages,-->
                    <!--                                inputsAboveSlider: true,-->
                    <!--                                unit: '',-->
                    <!--                                roundValues: true,-->
                    <!--                                sliderWidth: 300,-->
                    <!--                            }}-->
                    <!--                            onChange={val => {-->
                    <!--                                app.getOptions().max_vehicle_capacity_packages = val;-->
                    <!--                                app.triggerChange();-->
                    <!--                                app = app; // Trigger reactivity-->
                    <!--                                calcNuclei(false);-->
                    <!--                            }}-->
                    <!--                        />-->
                    <!--                    </div>-->
                    <!--                </div>-->
                    <!--                <div class="zone-param">-->
                    <!--                    <span>warehouse_work_time_minutes</span>-->
                    <!--                    <div class="slider">-->
                    <!--                        <TelematicsSliderSingle-->
                    <!--                            props={{-->
                    <!--                                min: app.getOptionsDetails('warehouse_work_time_minutes').min,-->
                    <!--                                max: app.getOptionsDetails('warehouse_work_time_minutes').max,-->
                    <!--                                currentValue: app.getOptions().warehouse_work_time_minutes,-->
                    <!--                                inputsAboveSlider: true,-->
                    <!--                                unit: '',-->
                    <!--                                roundValues: true,-->
                    <!--                                sliderWidth: 300,-->
                    <!--                            }}-->
                    <!--                            onChange={val => {-->
                    <!--                                app.getOptions().warehouse_work_time_minutes = val;-->
                    <!--                                app.triggerChange();-->
                    <!--                                app = app; // Trigger reactivity-->
                    <!--                                calcNuclei(false);-->
                    <!--                            }}-->
                    <!--                        />-->
                    <!--                    </div>-->
                    <!--                </div>-->
                    <pre>{JSON.stringify(app.getOptions(), null, 2)}</pre>
                </div>
            {/if}

            <TemplateSettingsGeneral>
                <div slot="label" style="display: flex; gap: 10px; align-items: center;">
                    <div>Parametry ogólne</div>
                    <div></div>
                </div>
                <div slot="content">
                    <div class="zone-param">
                        <div class="slider">
                            <Switch
                                label="Użyj parametrów pojazdów ciężarowych (use_truck_params)"
                                checked={app.getOptions().use_truck_params}
                                on:change={e => {
                                    app.getOptions().use_truck_params = e.detail.checked;
                                    app.triggerChange();
                                    app = app; // Trigger reactivity
                                    calcNuclei(false);
                                }}
                            />
                        </div>
                    </div>
                    <div class="zone-param">
                        <span>Czas wyliczania VRP (sugerowany, sekundy)</span>
                        <div class="slider">
                            <TelematicsSliderSingle
                                props={{
                                    min: app.getOptionsDetails('duration_seconds').min,
                                    max: app.getOptionsDetails('duration_seconds').max,
                                    currentValue: app.getOptions().duration_seconds,
                                    inputsAboveSlider: true,
                                    unit: 'sek.',
                                    roundValues: true,
                                    sliderWidth: 300,
                                }}
                                onChange={val => {
                                    app.getOptions().duration_seconds = val;
                                    app.triggerChange();
                                    app = app; // Trigger reactivity
                                    calcNuclei(false);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </TemplateSettingsGeneral>


            <TemplateSettingsGeneral>
                <div slot="label" style="display: flex; gap: 10px; align-items: center;">
                    <div>Parametry pojazdów</div>
                    <div></div>
                </div>
                <div slot="content">
                    <div class="zone-param">
                        <Switch
                            label="Uwzględniaj okna czasowe dla zleceń"
                            checked={app.getOptions().time_windows_jobs_enabled}
                            on:change={e => {
                                app.getOptions().time_windows_jobs_enabled = e.detail.checked;
                                app.triggerChange();
                                app = app; // Trigger reactivity
                            }}
                        />
                    </div>
                    <div class="zone-param">
                        <Switch
                            label="special_dedicated_fleet_46963"
                            checked={app.getOptions().special_dedicated_fleet_46963}
                            on:change={e => {
                                app.getOptions().special_dedicated_fleet_46963 = e.detail.checked;
                                if (app.getOptions().special_dedicated_fleet_46963) {
                                    app.getOptions().vehicle_mode = VEHICLE_MODE.MULTI;
                                    app.getOptions().depot_mode = DEPOT_MODE.SINGLE;
                                    app.getOptions().capacity_type = 'kg';
                                }
                                app.triggerChange();
                                app = app; // Trigger reactivity
                                calcNuclei(false);
                            }}
                        />
                    </div>
                    <div class="zone-param">
                        <Switch
                            label="Grupowanie zleceń"
                            checked={app.getOptions().grouping_enabled}
                            on:change={e => {
                                app.getOptions().grouping_enabled = e.detail.checked;
                                app.triggerChange();
                                app = app; // Trigger reactivity
                            }}
                        />
                    </div>
                    <div class="zone-param">
                        <span>Tryb pojazdów</span>
                        <div class="slider">
                            <Select
                                disabled={app.getOptions().special_dedicated_fleet_46963}
                                items={[
                                    {
                                        id: VEHICLE_MODE.SINGLE,
                                        label: 'Jeden rodzaj pojazdów (tak jak pierwszy pojazd)',
                                    },
                                    {
                                        id: VEHICLE_MODE.MULTI,
                                        label: 'Wiele rodzajów pojazdów (wg capacity - kg / packages)',
                                    },
                                ]}
                                selected={app.getOptions().vehicle_mode}
                                onChange={selectedId => {
                                    app.getOptions().vehicle_mode = selectedId;
                                    app.triggerChange();
                                    app = app; // Trigger reactivity
                                    return true;
                                }}
                            />
                        </div>
                    </div>
                    <div class="zone-param">
                        <span>Tryb magazynów</span>
                        <div class="slider">
                            <Select
                                disabled={app.getOptions().special_dedicated_fleet_46963}
                                items={[
                                    {
                                        id: DEPOT_MODE.SINGLE,
                                        label: 'Jeden wspólny magazyn',
                                    },
                                    {
                                        id: DEPOT_MODE.MULTI,
                                        label: 'Każdy pojazd z własnym magazynem (wybierz, jeśli pojazdy mają różne okna czasowe)',
                                    },
                                ]}
                                selected={app.getOptions().depot_mode}
                                onChange={selectedId => {
                                    app.getOptions().depot_mode = selectedId;
                                    app.triggerChange();
                                    app = app; // Trigger reactivity
                                    return true;
                                }}
                            />
                        </div>
                    </div>
                    <div class="zone-param">
                        <span>Liczba pojazdów</span>
                        <div class="slider">
                            <TelematicsSliderSingle
                                props={{
                                    min: 1,
                                    max: maxAvailableVehicles,
                                    currentValue: app.getMaxVehicles(),
                                    inputsAboveSlider: true,
                                    unit: '',
                                    roundValues: true,
                                    sliderWidth: 300,
                                }}
                                onChange={value => {
                                    if (availableVehicles === null) {
                                        console.warn('Available vehicles are not loaded yet.');
                                        return;
                                    }
                                    app.setMinVehicles(value);
                                    app.setMaxVehicles(value);
                                    app.triggerChange();
                                    calcNuclei(true);
                                }}
                            />
                            <!--                            <TelematicsSliderRange-->
                            <!--                                props={{-->
                            <!--                                    min: 1,-->
                            <!--                                    max: availableVehicles.length,-->
                            <!--                                    currentValue1: app.getMinVehicles(),-->
                            <!--                                    currentValue2: app.getMaxVehicles(),-->
                            <!--                                    inputsAboveSlider: true,-->
                            <!--                                    unit: '',-->
                            <!--                                    roundValues: true,-->
                            <!--                                    sliderWidth: 300,-->
                            <!--                                }}-->
                            <!--                                onChange={(value1, value2) => {-->
                            <!--                                    if (availableVehicles === null) {-->
                            <!--                                        console.warn('Available vehicles are not loaded yet.');-->
                            <!--                                        return;-->
                            <!--                                    }-->
                            <!--                                    app.setMinVehicles(value1 === false ? 1 : Math.round(value1));-->
                            <!--                                    app.setMaxVehicles(-->
                            <!--                                        value2 === false-->
                            <!--                                            ? availableVehicles.length-->
                            <!--                                            : Math.round(value2)-->
                            <!--                                    );-->
                            <!--                                    calcNuclei(true);-->
                            <!--                                }}-->
                            <!--                            />-->
                        </div>
                    </div>

                    <div class="zone-param">
                        <span>Procentowa modyfikacja liczby godzin pracy pojazdów (%)</span>
                        <div class="slider">
                            <TelematicsSliderSingle
                                props={{
                                    min: app.getOptionsDetails(
                                        'percentage_change_warehouse_working_hours'
                                    ).min,
                                    max: app.getOptionsDetails(
                                        'percentage_change_warehouse_working_hours'
                                    ).max,
                                    currentValue:
                                        app.getOptions().percentage_change_warehouse_working_hours,
                                    inputsAboveSlider: true,
                                    unit: '%',
                                    roundValues: true,
                                    sliderWidth: 300,
                                }}
                                onChange={val => {
                                    app.getOptions().percentage_change_warehouse_working_hours =
                                        val;
                                    app.triggerChange();
                                    app = app; // Trigger reactivity
                                    calcNuclei(false);
                                }}
                            />
                        </div>
                    </div>


                    <div class="zone-param">
                        <span>Procentowa modyfikacja czasu zakończenia okna czasowego (%)</span>
                        <div class="slider">
                            <TelematicsSliderSingle
                                props={{
                                    min: app.getOptionsDetails(
                                        'percentage_change_end_of_time_window'
                                    ).min,
                                    max: app.getOptionsDetails(
                                        'percentage_change_end_of_time_window'
                                    ).max,
                                    currentValue:
                                        app.getOptions().percentage_change_end_of_time_window,
                                    inputsAboveSlider: true,
                                    unit: '%',
                                    roundValues: true,
                                    sliderWidth: 300,
                                }}
                                onChange={val => {
                                    app.getOptions().percentage_change_end_of_time_window =
                                        val;
                                    app.triggerChange();
                                    app = app; // Trigger reactivity
                                    calcNuclei(false);
                                }}
                            />
                        </div>
                    </div>

                    <div class="zone-param" style="padding: 10px 0;">
                        <span>Co traktować jako graniczną ładowność pojazdu?</span>
                        <div class="slider">
                            <Select
                                items={[
                                    { id: 'kg', label: 'Ładowność w kg' },
                                    { id: 'packages', label: 'Ładowność w paczkach/paletach' },
                                ]}
                                disabled={app.getOptions().special_dedicated_fleet_46963}
                                selected={app.getOptions().capacity_type}
                                onChange={selectedId => {
                                    app.getOptions().capacity_type = selectedId;
                                    app.triggerChange();
                                    app = app; // Trigger reactivity
                                    calcNuclei(false);
                                    return true;
                                }}
                            />
                        </div>
                    </div>

                    {#if app.getOptions().capacity_type === 'kg'}
                        <div class="zone-param">
                            <span
                            >Procentowa modyfikacja limitu ładowności pojazdu (%, dotyczy kg)</span
                            >
                            <div class="slider">
                                <TelematicsSliderSingle
                                    props={{
                                        min: app.getOptionsDetails('percentage_change_capacity_kg')
                                            .min,
                                        max: app.getOptionsDetails('percentage_change_capacity_kg')
                                            .max,
                                        currentValue:
                                            app.getOptions().percentage_change_capacity_kg,
                                        inputsAboveSlider: true,
                                        unit: '%',
                                        roundValues: true,
                                        sliderWidth: 300,
                                    }}
                                    onChange={val => {
                                        app.getOptions().percentage_change_capacity_kg = val;
                                        app.triggerChange();
                                        app = app; // Trigger reactivity
                                        calcNuclei(false);
                                    }}
                                />
                            </div>
                        </div>
                    {/if}

                    {#if app.getOptions().capacity_type === 'packages'}
                        <div class="zone-param">
                            <span
                            >Procentowa modyfikacja limitu ładowności pojazdu (%, dotyczy
                                paczek/palet)</span
                            >
                            <div class="slider">
                                <TelematicsSliderSingle
                                    props={{
                                        min: app.getOptionsDetails(
                                            'percentage_change_capacity_packages'
                                        ).min,
                                        max: app.getOptionsDetails(
                                            'percentage_change_capacity_packages'
                                        ).max,
                                        currentValue:
                                            app.getOptions().percentage_change_capacity_packages,
                                        inputsAboveSlider: true,
                                        unit: '%',
                                        roundValues: true,
                                        sliderWidth: 300,
                                    }}
                                    onChange={val => {
                                        app.getOptions().percentage_change_capacity_packages = val;
                                        app.triggerChange();
                                        app = app; // Trigger reactivity
                                        calcNuclei(false);
                                    }}
                                />
                            </div>
                        </div>
                    {/if}
                </div>
            </TemplateSettingsGeneral>

            {#if false}
                <!-- not used, open hours from here are used only in depot_single mode  -->
                <TemplateSettingsGeneral>
                    <div slot="label" style="display: flex; gap: 10px; align-items: center;">
                        <div>Parametry czasu pracy</div>
                        <div>
                            <IconSettings
                                on:click={() => {
                                    openModal(ModalCustom, {
                                        component: ModalSettingsRouteSendTimeWrapper,
                                        componentProps: {
                                            update: () => {
                                                SettingsManager.updateCustomer();
                                                app.getOptions().start_time_from_warehouse =
                                                    $customerSettings.planning.start_time_route;
                                                app = app;
                                            },
                                        },
                                    });
                                }}
                                scale={0.6}
                            />
                        </div>
                    </div>
                    <div slot="content">
                        <div class="zone-param">
                            <div>
                                Czas pracy pojazdów [min.] {app.getOptions()
                                .start_time_from_warehouse} -
                                {convertMinutesToStringTime(
                                    convertStringTimeToMinutes(
                                        app.getOptions().start_time_from_warehouse
                                    ) + app.getOptions().warehouse_work_time_minutes
                                )} ({calcSumHoursFromOpenHours({
                                open: app.getOptions().start_time_from_warehouse,
                                close: convertMinutesToStringTime(
                                    convertStringTimeToMinutes(
                                        app.getOptions().start_time_from_warehouse
                                    ) + app.getOptions().warehouse_work_time_minutes
                                ),
                            })})
                            </div>
                            <div class="slider">
                                <TelematicsSliderSingle
                                    props={{
                                        min: app.getOptionsDetails('warehouse_work_time_minutes')
                                            .min,
                                        max: app.getOptionsDetails('warehouse_work_time_minutes')
                                            .max,
                                        currentValue: app.getOptions().warehouse_work_time_minutes,
                                        inputsAboveSlider: true,
                                        unit: 'min.',
                                        roundValues: true,
                                        sliderWidth: 300,
                                    }}
                                    onChange={val => {
                                        app.getOptions().warehouse_work_time_minutes = val;
                                        app.triggerChange();
                                        app = app; // Trigger reactivity
                                        calcNuclei(false);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </TemplateSettingsGeneral>
            {/if}

            <TemplateSettingsGeneral>
                <div slot="label" style="display: flex; gap: 10px; align-items: center;">
                    <div>Parametry czasu serwisowego</div>
                    <div>
                        <IconSettings
                            on:click={() => {
                                openModal(ModalCustom, {
                                    component: ModalSettingsServiceTimeWrapper,
                                    componentProps: {
                                        update: () => {
                                            SettingsManager.updateCustomer();
                                            app.getOptions().service_time_on_stop =
                                                $customerSettings.each_stop_minutes * 60;
                                            app.getOptions().service_time_per_package =
                                                $customerSettings.each_delivery_minutes * 60;
                                            app = app;
                                        },
                                    },
                                });
                            }}
                            scale={0.6}
                        />
                    </div>
                </div>
                <div slot="content">
                    <div class="zone-param">
                        <span
                        >Procentowa modyfikacja czasu serwisowego na stop ({(
                            app.getOptions()?.service_time_on_stop ?? 0
                        ).toFixed(2) +
                        ' * ' +
                        app.getOptions().percentage_change_service_time_on_stop.toFixed(0) +
                        '% = ' +
                        (
                            ((app.getOptions().percentage_change_service_time_on_stop +
                                    100) /
                                100) *
                            (app.getOptions()?.service_time_on_stop ?? 0)
                        ).toFixed(0)} sek.)</span
                        >
                        <div class="slider">
                            <TelematicsSliderSingle
                                props={{
                                    min: app.getOptionsDetails(
                                        'percentage_change_service_time_on_stop'
                                    ).min,
                                    max: app.getOptionsDetails(
                                        'percentage_change_service_time_on_stop'
                                    ).max,
                                    currentValue:
                                        app.getOptions().percentage_change_service_time_on_stop,
                                    inputsAboveSlider: true,
                                    unit: '%',
                                    roundValues: true,
                                    sliderWidth: 300,
                                }}
                                onChange={val => {
                                    app.getOptions().percentage_change_service_time_on_stop = val;
                                    app.triggerChange();
                                    app = app; // Trigger reactivity
                                    calcNuclei(false);
                                }}
                            />
                        </div>
                    </div>
                    <div class="zone-param">
                        <span
                        >Procentowa modyfikacja czasu serwisowego na paczkę ({(
                            app.getOptions()?.service_time_per_package ?? 0
                        ).toFixed(2) +
                        ' * ' +
                        app
                            .getOptions()
                            .percentage_change_service_time_per_package.toFixed(0) +
                        '% = ' +
                        (
                            ((app.getOptions().percentage_change_service_time_per_package +
                                    100) /
                                100) *
                            (app.getOptions()?.service_time_per_package ?? 0)
                        ).toFixed(0)} sek.)
                        </span>
                        <div class="slider">
                            <TelematicsSliderSingle
                                props={{
                                    min: app.getOptionsDetails(
                                        'percentage_change_service_time_per_package'
                                    ).min,
                                    max: app.getOptionsDetails(
                                        'percentage_change_service_time_per_package'
                                    ).max,
                                    currentValue:
                                        app.getOptions().percentage_change_service_time_per_package,
                                    inputsAboveSlider: true,
                                    unit: '%',
                                    roundValues: true,
                                    sliderWidth: 300,
                                }}
                                onChange={val => {
                                    app.getOptions().percentage_change_service_time_per_package =
                                        val;
                                    app.triggerChange();
                                    app = app; // Trigger reactivity
                                    calcNuclei(false);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </TemplateSettingsGeneral>
            <TemplateSettingsGeneral>
                <div slot="label" style="display: flex; gap: 10px; align-items: center;">
                    <div>Parametry kosztu trasy</div>
                    <div>
                        <IconSettings
                            on:click={() => {
                                openModal(ModalCustom, {
                                    component: ModalSettingsRouteCostWrapper,
                                    componentProps: {
                                        update: () => {
                                            SettingsManager.updateCustomer();
                                            app.getOptions().cost_per_minute =
                                                $customerSettings.route_cost.per_min;
                                            app.getOptions().cost_per_km =
                                                $customerSettings.route_cost.per_km;
                                            app.getOptions().cost_per_item =
                                                $customerSettings.route_cost.per_item;
                                            app = app;
                                        },
                                    },
                                });
                            }}
                            scale={0.6}
                        />
                    </div>
                </div>
                <div slot="content">
                    <div class="zone-param">
                        <span
                        >Procentowa modyfikacja kosztu za minutę ({(
                            app.getOptions()?.cost_per_minute ?? 0
                        ).toFixed(2)} * {app
                            .getOptions()
                            .percentage_change_cost_per_minute.toFixed(0)}% = {(
                            ((app.getOptions().percentage_change_cost_per_minute + 100) / 100) *
                            (app.getOptions()?.cost_per_minute ?? 0)
                        ).toFixed(2)})</span
                        >
                        <div class="slider">
                            <TelematicsSliderSingle
                                props={{
                                    min: app.getOptionsDetails('percentage_change_cost_per_minute')
                                        .min,
                                    max: app.getOptionsDetails('percentage_change_cost_per_minute')
                                        .max,
                                    currentValue:
                                        app.getOptions().percentage_change_cost_per_minute,
                                    inputsAboveSlider: true,
                                    unit: '%',
                                    roundValues: true,
                                    sliderWidth: 300,
                                }}
                                onChange={val => {
                                    app.getOptions().percentage_change_cost_per_minute = val;
                                    app.triggerChange();
                                    app = app; // Trigger reactivity
                                    calcNuclei(false);
                                }}
                            />
                        </div>
                    </div>
                    <div class="zone-param">
                        <span
                        >Procentowa modyfikacja kosztu za km ({(
                            app.getOptions()?.cost_per_km ?? 0
                        ).toFixed(2)} * {app
                            .getOptions()
                            .percentage_change_cost_per_km.toFixed(0)}% = {(
                            ((app.getOptions().percentage_change_cost_per_km + 100) / 100) *
                            (app.getOptions()?.cost_per_km ?? 0)
                        ).toFixed(2)})</span
                        >
                        <div class="slider">
                            <TelematicsSliderSingle
                                props={{
                                    min: app.getOptionsDetails('percentage_change_cost_per_km').min,
                                    max: app.getOptionsDetails('percentage_change_cost_per_km').max,
                                    currentValue: app.getOptions().percentage_change_cost_per_km,
                                    inputsAboveSlider: true,
                                    unit: '%',
                                    roundValues: true,
                                    sliderWidth: 300,
                                }}
                                onChange={val => {
                                    app.getOptions().percentage_change_cost_per_km = val;
                                    app.triggerChange();
                                    app = app; // Trigger reactivity
                                    calcNuclei(false);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </TemplateSettingsGeneral>
        {/if}
    </div>
</div>

<style>
    .zone-param {
        font-size: 14px;
        margin: 10px 0 10px 20px;
    }

    .processing {
        opacity: 0.5;
        pointer-events: none;
    }

    :global(.multi-select-wide-container) {
        background-color: var(--section-background) !important;
    }
</style>
